import { Settings } from 'luxon'
import * as types from '../mutation-types'
import i18n from '../../i18n'
import uuid4 from 'uuid/v4'
import { router } from '@/router'
import GTM from '@/utils/gtm'
import { formatUUID } from '@/utils/formatUUID'
import { isStripeRedirect } from '@/utils/stripeRedirect'

// initial state
export const initialState = () => {
  return {
    frontendSessionId: uuid4(),
    activeStep: 1,
    campground: null,
    discount: null,
    currency: {},
    langSlug: null,
    language: {},
    loader: false,
    showBookingDetails: false,
    testmode: null,
    steps: {
      1: {
        displaySelection: 'dates',
        fulfilled: false,
        id: 1,
      },
      2: {
        id: 2,
        selectedCampsiteType: null,
        selectedCampsiteIsAvailable: true,
        fulfilled: false,
      },
      3: {
        id: 3,
        fulfilled: true,
      },
      4: {
        id: 4,
        formPart1Passed: false,
        fulfilled: false,
      },
      5: {
        id: 5,
        fulfilled: false,
      },
    },
    pageLoader: false,
  }
}
let state = initialState()

// getters
const getters = {
  testmode: state => state.testmode,
  activeStep: state => state.activeStep,
  campground: state => state.campground,
  currency: state => state.currency,
  langSlug: state => state.langSlug,
  language: state => state.language,
  loader: state => state.loader,
  showBookingDetails: state => state.showBookingDetails,
  steps: state => state.steps,
  frontendSessionId: state => state.frontendSessionId,
  discountValue: state => state.discount,
  pageLoader: state => state.pageLoader,
}

// actions
const actions = {
  setTestmode({ commit }, value) {
    commit(types.APP_SET_TESTMODE, value)
  },
  setLanguageSlug({ commit }, value) {
    commit(types.APP_SET_LANGUAGE_SLUG, value)
  },
  setLanguage({ commit, dispatch }, value) {
    commit(types.APP_SET_LANGUAGE, value)
    dispatch('campground/checkPrice', false, { root: true })
  },
  setCurrency({ commit, dispatch, rootGetters }, currency) {
    commit(types.APP_SET_LOADER, true)
    const dates = rootGetters['booking/dates']
    const persons = rootGetters['booking/selectedPersons']
    const isCompleted = rootGetters['booking/isCompleted']

    /* no need to do any price or availability checks is booking is completed */
    if (isCompleted) {
      commit(types.APP_SET_CURRENCY, currency)
      commit(types.APP_SET_LOADER, false)
      return
    }

    if (dates && dates.length === 2 && persons.length > 0) {
      Promise.all([
        dispatch('campground/getExtraPrices', currency, { root: true }),
        dispatch('campground/getAvailabilities', currency, { root: true }),
        dispatch('campground/checkPrice', currency, { root: true }),
      ]).then(() => {
        commit(types.APP_SET_CURRENCY, currency)
        commit(types.APP_SET_LOADER, false)
      })
    } else {
      const isPublished = rootGetters['campground/isPublished']
      if(!isPublished) return
      dispatch('campground/getExtraPrices', currency, { root: true })
      commit(types.APP_SET_CURRENCY, currency)
      commit(types.APP_SET_LOADER, false)
    }
  },
  setCampground({ commit }, value) {
    commit(types.APP_SET_CAMPGROUND, value)
  },
  setDiscountValue({ commit }, value) {
    commit(types.APP_SET_DISCOUNT_VALUE, value)
  },
  setStep({ commit, state, dispatch }, newStep) {
    const stripeRedirect = newStep === 5 && isStripeRedirect()
    /* if you have passed step 4 and return to a lower state, also rest fromPart1Passed */
    /* istanbul ignore else */
    if (state.activeStep >= 4 && newStep <= 4) {
      commit(types.APP_SET_STEP_STATUS, {
        number: 4,
        key: 'formPart1Passed',
        value: false,
      })
    }

    /* if newStep is payment - clear status - check stored checkout */
    /* istanbul ignore else */
    if (newStep === 5 && !stripeRedirect) {
      dispatch('booking/resetStatus', null, { root: true })
      dispatch('booking/handleStoredCheckout', null, { root: true })
    }

    /* in any case you where the new step isn't step 6 (confirmation), set completed to false */
    /* istanbul ignore else */
    if (newStep !== 6) {
      dispatch('booking/setCompleted', false, { root: true })
    }

    commit(types.APP_SET_STEP, newStep)
  },
  setStepStatus({ commit }, { number, key, value }) {
    commit(types.APP_SET_STEP_STATUS, { number, key, value })
  },
  setLoader({ commit }, value) {
    commit(types.APP_SET_LOADER, value)
  },
  toggleBookingDetails({ commit }) {
    commit(types.APP_TOGGLE_BOOKING_DETAILS)
  },
  resetStepStatus({ commit }) {
    const initState = initialState()
    commit(types.APP_RESET_STEP_STATUS, initState.steps)
  },
  resetState({ commit }) {
    const initState = initialState()
    commit(types.APP_RESET_STATE, initState)
  },
  setTitle({ commit, rootGetters }) {
    setTimeout(() => {
      if (
        rootGetters['campground/campground'] &&
        rootGetters['campground/campground'].name
      ) {
        commit(types.APP_SET_TITLE, rootGetters['campground/campground'].name)
      }
    }, 0)
  },
  trackEcommerceEvents({ rootGetters }, step) {
    const price = rootGetters['booking/price']
    const confirmation = rootGetters['booking/confirmation']
    const currency = rootGetters['application/currency']
    const discountValue = rootGetters['application/discountValue']
    const campground = rootGetters['campground/campground']
    const availableExtras = rootGetters['campground/availableExtras']
    const steps = rootGetters['application/steps']
    const selectedBookingType = rootGetters['booking/selectedBookingType']
    const userDetails = rootGetters['booking/billing']
    
    const selectedChoice = selectedBookingType === 'default' ? null : selectedBookingType;

    let actionField = {}
    if (step === 1) {
      actionField = { step: 1, option: 'Traveldate' }
    } else if (step === 2) {
      actionField = { step: 2, option: 'Pitch / Accommodation' }
    } else if (step === 3) {
      actionField = { step: 3, option: 'Extras' }
    } else if (step === 4) {
      if (steps[4].formPart1Passed && steps[4].fulfilled) {
        actionField = { step: 4, option: 'Recheck Booking' }
      } else if (steps[4].formPart1Passed) {
        actionField = { step: 4, option: 'Address information' }
      } else {
        actionField = { step: 4, option: 'Personal information' }
      }
    } else if (step === 5) {
      actionField = { step: 5, option: 'Payment' }
    } else if (step === 6) {
      actionField = {
        id: router.currentRoute.params.bookingId,
        revenue: price.total,
      }
      if (discountValue) {
        actionField.coupon = discountValue
      }
    }

    let selectedExtras = []
    let selectedExtrasGA4 = []
    availableExtras.forEach(extra => {
      confirmation.booking_data &&
        confirmation.booking_data.extras.forEach(e => {
          if (e.id === extra.id) {
            selectedExtras.push({
              name: extra.name,
              id: `${e.id}`,
              quantity: e.amount,
              price: extra.price,
            })
            selectedExtrasGA4.push({
              item_name: extra.name,
              item_id: `${e.id}`,
              item_brand: 'Max Camping',
              quantity: e.amount,
              price: extra.price,
            })
          }
        })
    })

    let products = [
      {
        name: campground && campground.name,
        id: campground && campground.slug,
        price: price.total,
        brand: 'ACSI Camping.info Booking',
        category: campground && campground.country,
        variant: campground && campground.city,
        quantity: 1,
      },
    ]

    let items = [
      {
        item_id: campground && campground.slug,
        item_name: campground && campground.name,
        item_brand: 'Max Camping',
        currency: currency.code,
        price: price.total,
        quantity: 1,
      },
    ]

    if (discountValue) {
      products[0].coupon = discountValue
      items[0].coupon = discountValue
    }
    let eventName = ''
    let userMeta = {};
    const user = {
      language: router.currentRoute.params.language,
      first_name: userDetails.name || "",
      last_name: userDetails.lastname || "",
      email: userDetails.mail || "",
      phone: userDetails.phone || "",
    };

    if (step === 1) {
      eventName = 'begin_checkout'
    } else if (step === 2) {
      eventName = 'checkout_select_accommodation_type'
    } else if (step === 3) {
      eventName = 'checkout_add_extras'
    } else if (step === 4) {
      if (steps[4].formPart1Passed && steps[4].fulfilled) {
        eventName = 'checkout_overview'
        userMeta.user = user
      } else if (steps[4].formPart1Passed) {
        eventName = 'add_shipping_info'
        userMeta.user = user
      } else {
        eventName = 'checkout_add_personal_info'
      }
    } else if (step === 5) {
      eventName = 'add_payment_info'
      userMeta.user = user
    }

    if (step === 6) {
      // Universal Analytics event
      GTM.track({
        event: 'booking',
        ecommerce: {
          currencyCode: currency.code,
          purchase: {
            actionField: actionField,
            products: products.concat(selectedExtras),
          },
          coupon: selectedChoice,
        },
        user,
      })
      // GA4 event
      GTM.track({
        event: 'purchase',
        ecommerce: {
          currency: currency.code,
          value: price.total,
          transaction_id: formatUUID(router.currentRoute.params.bookingId),
          items: items.concat(selectedExtrasGA4),
          coupon: selectedChoice,
        },
        user,
      })
    } else {
      // Universal Analytics event
      GTM.track({
        event: `checkout_step_${step}`,
        ecommerce: {
          currencyCode: currency.code,
          checkout: {
            actionField: actionField,
            products: products.concat(selectedExtras),
          },
          coupon: selectedChoice,
        },
        ...userMeta,
      })      
      // GA4 event
      GTM.track({
        event: eventName,
        ecommerce: {
          currency: currency.code,
          value: price.total,
          items: items.concat(selectedExtrasGA4),
          coupon: selectedChoice,
        },
        ...userMeta,
      })
    }
  },
  setPageLoader({ commit }, value) {
    commit(types.APP_SET_PAGE_LOADER, value)
  },
}

// mutations
const mutations = {
  [types.APP_RESET_STATE](state, initState) {
    Object.keys(initState).forEach(key => {
      state[key] = initState[key]
    })
  },
  [types.APP_SET_CURRENCY](state, value) {
    state.currency = value
  },
  [types.APP_SET_TESTMODE](state, value) {
    state.testmode = value
  },
  [types.APP_SET_LANGUAGE_SLUG](state, value) {
    state.langSlug = value
    i18n.locale = value
    Settings.defaultLocale = value
  },
  [types.APP_SET_LANGUAGE](state, value) {
    state.language = value
  },
  [types.APP_SET_CAMPGROUND](state, value) {
    state.campground = value
  },
  [types.APP_SET_DISCOUNT_VALUE](state, value) {
    state.discount = value
  },
  [types.APP_SET_STEP](state, payload) {
    state.activeStep = payload
  },
  [types.APP_SET_LOADER](state, payload) {
    state.loader = payload
  },
  [types.APP_TOGGLE_BOOKING_DETAILS](state) {
    state.showBookingDetails = !state.showBookingDetails
  },
  [types.APP_SET_STEP_STATUS](state, { number, key, value }) {
    const step = { ...state.steps[number], [key]: value }
    state.steps = { ...state.steps, [number]: step }
  },
  [types.APP_RESET_STEP_STATUS](state, steps) {
    state.steps = steps
  },
  [types.APP_SET_TITLE](state, campgroundName) {
    if (campgroundName) {
      document.title = `${i18n.t('meta.title', { campground: campgroundName })}`
    }
  },
  [types.APP_SET_PAGE_LOADER](state, payload) {
    state.pageLoader = payload
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
